"use client";

import { createLogAdapter } from "@/lib/logger/console-adapter";

const uiLog = createLogAdapter("lib/favorites/favorites.ts");

export interface FavoriteTokenSummary {
	id: string;
	name: string;
	ticker: string;
	price: number;
	holder_count?: number;
	comment_count?: number;
}

export const DEFAULT_FAVORITE_TOKEN_ID = "2rfi";

const DEFAULT_FAVORITE_TOKEN_FALLBACK: FavoriteTokenSummary = {
	id: DEFAULT_FAVORITE_TOKEN_ID,
	name: "BabyOdin",
	ticker: "BabyOdin",
	price: 0,
	holder_count: 0,
	comment_count: 0,
};

interface FavoriteTokenRecord {
	user: string;
	token: string;
	date_created: string;
}

interface FavoritesResponse {
	data: FavoriteTokenRecord[];
	page: number;
	limit: number;
	count: number;
}

interface GuestFavoriteTokenRecord extends FavoriteTokenSummary {
	date_created: string;
	cached_at: string;
}

export type FavoritesUpdateSource = "guest" | "server";

interface FavoritesUpdatedDetail {
	source: FavoritesUpdateSource;
	principal?: string;
}

const GUEST_FAVORITES_STORAGE_KEY = "guest_token_favorites_v1";
const FAVORITES_UPDATED_EVENT = "odin-baby:favorites-updated";
export const MAX_GUEST_FAVORITES = 100;

function canUseDom() {
	return typeof window !== "undefined";
}

function parseGuestFavorites(raw: string | null): GuestFavoriteTokenRecord[] {
	if (!raw) return [];

	try {
		const parsed = JSON.parse(raw) as unknown;
		if (!Array.isArray(parsed)) return [];

		return parsed.filter((item): item is GuestFavoriteTokenRecord => {
			if (!item || typeof item !== "object") return false;
			const record = item as Partial<GuestFavoriteTokenRecord>;
			return (
				typeof record.id === "string" &&
				typeof record.name === "string" &&
				typeof record.ticker === "string" &&
				typeof record.price === "number"
			);
		});
	} catch (error) {
		uiLog.error("Failed to parse guest favorites:", error);
		return [];
	}
}

function persistGuestFavorites(records: GuestFavoriteTokenRecord[]) {
	if (!canUseDom()) return;

	try {
		window.localStorage.setItem(
			GUEST_FAVORITES_STORAGE_KEY,
			JSON.stringify(records),
		);
	} catch (error) {
		uiLog.error("Failed to persist guest favorites:", error);
	}
}

export function readGuestFavoriteTokens(): FavoriteTokenSummary[] {
	if (!canUseDom()) return [];

	return parseGuestFavorites(
		window.localStorage.getItem(GUEST_FAVORITES_STORAGE_KEY),
	).map(({ cached_at: _cachedAt, date_created: _dateCreated, ...token }) => token);
}

export function isGuestFavoriteToken(tokenId: string): boolean {
	return readGuestFavoriteTokens().some((token) => token.id === tokenId);
}

export function upsertGuestFavoriteToken(
	token: FavoriteTokenSummary,
): { trimmed: boolean } {
	if (!canUseDom()) return { trimmed: false };

	const now = new Date().toISOString();
	const existing = parseGuestFavorites(
		window.localStorage.getItem(GUEST_FAVORITES_STORAGE_KEY),
	);
	const nextRecord: GuestFavoriteTokenRecord = {
		...token,
		date_created:
			existing.find((item) => item.id === token.id)?.date_created ?? now,
		cached_at: now,
	};
	const filtered = existing.filter((item) => item.id !== token.id);
	const nextRecords = [nextRecord, ...filtered];
	const trimmed = nextRecords.length > MAX_GUEST_FAVORITES;

	persistGuestFavorites(nextRecords.slice(0, MAX_GUEST_FAVORITES));
	return { trimmed };
}

export function removeGuestFavoriteToken(tokenId: string): void {
	if (!canUseDom()) return;

	const existing = parseGuestFavorites(
		window.localStorage.getItem(GUEST_FAVORITES_STORAGE_KEY),
	);
	persistGuestFavorites(existing.filter((item) => item.id !== tokenId));
}

export function clearGuestFavoriteTokens(): void {
	if (!canUseDom()) return;
	window.localStorage.removeItem(GUEST_FAVORITES_STORAGE_KEY);
}

export function dispatchFavoritesUpdated(detail: FavoritesUpdatedDetail): void {
	if (!canUseDom()) return;

	window.dispatchEvent(
		new CustomEvent<FavoritesUpdatedDetail>(FAVORITES_UPDATED_EVENT, {
			detail,
		}),
	);
}

export function subscribeFavoritesUpdated(
	callback: (detail: FavoritesUpdatedDetail) => void,
): () => void {
	if (!canUseDom()) return () => undefined;

	const handleCustomEvent = (event: Event) => {
		const customEvent = event as CustomEvent<FavoritesUpdatedDetail>;
		callback(customEvent.detail);
	};

	const handleStorage = (event: StorageEvent) => {
		if (event.key !== GUEST_FAVORITES_STORAGE_KEY) return;
		callback({ source: "guest" });
	};

	window.addEventListener(FAVORITES_UPDATED_EVENT, handleCustomEvent as EventListener);
	window.addEventListener("storage", handleStorage);

	return () => {
		window.removeEventListener(
			FAVORITES_UPDATED_EVENT,
			handleCustomEvent as EventListener,
		);
		window.removeEventListener("storage", handleStorage);
	};
}

export async function fetchFavoriteTokenRecords(
	principal: string,
	limit: number,
): Promise<FavoritesResponse> {
	const response = await fetch(
		`https://api.odin.fun/v1/user/${principal}/token/favorites?page=1&limit=${limit}`,
	);

	if (!response.ok) {
		throw new Error(`Failed to fetch favorites: ${response.status}`);
	}

	return (await response.json()) as FavoritesResponse;
}

export async function fetchFavoriteTokenIdsForPrincipal(
	principal: string,
	limit: number = 1000,
): Promise<Set<string>> {
	const favoritesData = await fetchFavoriteTokenRecords(principal, limit);
	return new Set((favoritesData.data || []).map((favorite) => favorite.token));
}

export async function fetchFavoriteTokensForPrincipal(
	principal: string,
	limit: number,
): Promise<FavoriteTokenSummary[]> {
	const favoritesData = await fetchFavoriteTokenRecords(principal, limit);
	const tokensWithDetails = await Promise.all(
		(favoritesData.data || []).map((favorite) =>
			fetchTokenSummaryById(favorite.token),
		),
	);

	return tokensWithDetails.filter(
		(token): token is FavoriteTokenSummary => token !== null,
	);
}

export async function fetchTokenSummaryById(
	tokenId: string,
): Promise<FavoriteTokenSummary | null> {
	try {
		const tokenResponse = await fetch(`https://api.odin.fun/v1/token/${tokenId}`);
		if (!tokenResponse.ok) return null;
		return (await tokenResponse.json()) as FavoriteTokenSummary;
	} catch (error) {
		uiLog.error(`Error fetching token ${tokenId}:`, error);
		return null;
	}
}

export async function getDefaultFavoriteToken(): Promise<FavoriteTokenSummary> {
	const token =
		(await fetchTokenSummaryById(DEFAULT_FAVORITE_TOKEN_ID)) ??
		DEFAULT_FAVORITE_TOKEN_FALLBACK;
	return token;
}

export function mergeFavoriteTokensWithDefaults(
	favorites: FavoriteTokenSummary[],
	defaultTokens: FavoriteTokenSummary[],
	limit: number,
): FavoriteTokenSummary[] {
	const merged = [...favorites];
	const existingIds = new Set(favorites.map((token) => token.id));

	for (const token of defaultTokens) {
		if (existingIds.has(token.id)) continue;
		merged.unshift(token);
		existingIds.add(token.id);
	}

	return merged.slice(0, limit);
}
